import React, { useState, useMemo, useEffect, useCallback } from 'react';
import QuickActionCard from './QuickActionCard';
import FiringAlertsWelcomeCard from './FiringAlertsWelcomeCard';
import SuggestionChip from './SuggestionChip';
import StatusBadge from './StatusBadge';
import { useTheme } from '../../context/ThemeContext';
import { alertApi } from '../../services/alertApi';
import dayjs from 'dayjs';
import { isFiringOnlyAlertState } from '../../utils/alertDisplayHelpers';
import './WelcomeScreen.css';

function getAlertName(alert) {
  const labels = alert.labels || {};

  if (alert.name === 'alertname' || alert.name === 'rulename') {
    return labels.rulename || labels.alertname || null;
  }

  if (alert.name && alert.name.trim() !== '' && alert.name !== 'alertname' && alert.name !== 'rulename') {
    return alert.name;
  }

  return labels.rulename || labels.alertname || null;
}

const WelcomeScreen = ({ onQuickAction, greetingText = '', middleComposer = null }) => {
  const { isDark } = useTheme();
  const [firingAlerts, setFiringAlerts] = useState([]);
  const [loadingAlerts, setLoadingAlerts] = useState(false);

  const fetchFiringAlerts = useCallback(async () => {
    try {
      setLoadingAlerts(true);
      let awsAccountId = "";
      try {
        awsAccountId =
          localStorage.getItem("clairai_selected_aws_account_id") ||
          localStorage.getItem("selectedAwsAccountId") ||
          "";
      } catch {
        awsAccountId = "";
      }
      const response = await alertApi.getFiringAlerts({
        timeoutMs: 8000,
        awsAccountId: awsAccountId || undefined,
      });
      if (response.status === 'success' && Array.isArray(response.alerts)) {
        setFiringAlerts(response.alerts);
      } else {
        setFiringAlerts([]);
      }
    } catch (err) {
      console.error('Error fetching firing alerts:', err);
      setFiringAlerts([]);
    } finally {
      setLoadingAlerts(false);
    }
  }, []);

  useEffect(() => {
    fetchFiringAlerts();
  }, [fetchFiringAlerts]);

  const tableData = useMemo(() => {
    // Keep all currently firing alerts; activeAt is episode start and may be old/missing.
    const recentAlerts = firingAlerts.filter((alert) => {
      if (!isFiringOnlyAlertState(alert.state)) {
        return false;
      }

      const alertName = getAlertName(alert);

      if (!alertName || alertName.trim() === '') {
        return false;
      }

      if (alertName === 'alertname' || alertName === 'rulename') {
        const labels = alert.labels || {};
        const betterName = labels.rulename || labels.alertname;
        if (!betterName || betterName.trim() === '') {
          return false;
        }
      }

      return true;
    });

    const alertMap = {};
    recentAlerts.forEach((alert) => {
      const alertName = getAlertName(alert);
      if (!alertName) return;

      const trimmedName = alertName.trim();
      const activeAtString = alert.activeAt;

      const activeAt = activeAtString ? dayjs(activeAtString) : null;
      const displayTime = activeAtString
        ? dayjs(activeAtString).format('MMM DD, YYYY HH:mm:ss')
        : 'Unknown';

      if (!alertMap[trimmedName]) {
        alertMap[trimmedName] = {
          name: trimmedName,
          startedRaw: activeAt,
          started: displayTime,
          count: 1,
        };
      } else {
        if (activeAt && (!alertMap[trimmedName].startedRaw || activeAt.isAfter(alertMap[trimmedName].startedRaw))) {
          alertMap[trimmedName].startedRaw = activeAt;
          alertMap[trimmedName].started = displayTime;
        }
        alertMap[trimmedName].count += 1;
      }
    });

    return Object.values(alertMap)
      .sort((a, b) => {
        if (!a.startedRaw && !b.startedRaw) return 0;
        if (!a.startedRaw) return 1;
        if (!b.startedRaw) return -1;
        return b.startedRaw.valueOf() - a.startedRaw.valueOf();
      })
      .map((alert, index) => ({
        key: index,
        ...alert,
      }));
  }, [firingAlerts]);

  const handleAlertSelect = (alertName) => {
    const prompt = `Why is the alert "${alertName}" firing?`;
    onQuickAction(prompt, 'auto v1');
  };

  const quickActions = [
    {
      id: 'errors',
      icon: '⚠️',
      title: 'Check Errors',
      subtitle: 'Recent failures & issues',
      color: '#ff4d4f',
      query: 'Show me recent errors and failures in my AWS infrastructure',
      model: 'rca-v1',
    },
    {
      id: 'resources',
      icon: '🖥️',
      title: 'Resources',
      subtitle: 'ClairAI platform infra',
      color: '#1890ff',
      query: 'Show me ClairAI platform infrastructure health for the EKS cluster',
      model: 'rca-v1',
    },
    {
      id: 'performance',
      icon: '📊',
      title: 'Performance',
      subtitle: 'Service health metrics',
      color: '#52c41a',
      query: 'Show me performance metrics and service health status',
      model: 'rca-v1',
    },
    {
      id: 'analytics',
      icon: '📈',
      title: 'Analytics',
      subtitle: 'Cost & usage patterns',
      color: '#fa8c16',
      query: 'Show me cost breakdown and usage patterns for this month',
      model: 'finops-v1',
    },
  ];

  const suggestions = [
    { text: 'Recent errors', query: 'Show me recent failures', model: 'rca-v1' },
    { text: 'High latency services', query: 'Which services have high latency?', model: 'rca-v1' },
    { text: 'Cost breakdown', query: 'Show me cost breakdown by service for this month', model: 'finops-v1' },
  ];

  const statusBadges = [
    { name: 'Prometheus', status: 'online' },
    { name: 'Loki', status: 'online' },
  ];

  return (
    <div className="ai-welcome-root" style={{ backgroundColor: isDark ? '#0d1117' : '#fafafa' }}>
      <header className={`ai-welcome-banner ${isDark ? 'dark' : ''}`}>
        <div className="ai-welcome-banner-inner">
          <div className="ai-welcome-banner-icon">✨</div>
          <h1 className="ai-welcome-banner-title">ClairAI Assistant · Clairai-Copilot</h1>
        </div>
        <p className="ai-welcome-banner-sub">Root-cause analysis for metrics, logs, and alerts</p>
      </header>

      <div className="ai-welcome-scroll">
        <p className="ai-welcome-intro" style={{ color: isDark ? '#adbac7' : '#595959' }}>
          Ask focused questions about service health, failing alerts, error spikes, latency regressions, or cost anomalies, and I will correlate Prometheus metrics with Loki logs to explain what changed.
        </p>
        {!!greetingText && (
          <p className="ai-welcome-greeting" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>
            {greetingText}
          </p>
        )}
        {middleComposer && (
          <div className="ai-welcome-middle-composer">
            {middleComposer}
          </div>
        )}

        <div className="ai-welcome-cards">
          {quickActions.map((action) => (
            <QuickActionCard
              key={action.id}
              icon={action.icon}
              title={action.title}
              subtitle={action.subtitle}
              color={action.color}
              onClick={() => onQuickAction(action.query, action.model)}
              isDark={isDark}
            />
          ))}
          <FiringAlertsWelcomeCard
            isDark={isDark}
            loading={loadingAlerts}
            rows={tableData}
            onAlertClick={handleAlertSelect}
          />
        </div>

        <div className="ai-welcome-badges">
          {statusBadges.map((badge) => (
            <StatusBadge
              key={badge.name}
              name={badge.name}
              status={badge.status}
              isDark={isDark}
            />
          ))}
        </div>
      </div>

      <div className={`ai-welcome-chips ${isDark ? 'dark' : ''}`}>
        <div className="ai-welcome-chips-inner">
          {suggestions.map((suggestion, index) => (
            <SuggestionChip
              key={index}
              text={suggestion.text}
              onClick={() => onQuickAction(suggestion.query, suggestion.model)}
              isDark={isDark}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreen;
